import {Button, Form, Input, Modal, Space} from "antd";
import {useState} from "react";
import {startAuthentication} from "@simplewebauthn/browser";
import {RegisterControl} from "components/Account/RegisterDialog";
import {VerticalSpace} from "components/Layout/VerticalSpace";
import {setToken, useOrcIdLogin} from "utilities/auth";
import {useMutation} from "@apollo/client";
import {CreateAssertionOptionsDocument, CreateTokenDocument} from "generated/graphql";

interface Props {
  open: boolean
  onClose: (cancel?: boolean) => void
}

export const LoginDialog = ({open, onClose}: Props) => {
  const [form] = Form.useForm();
  const [register, setRegister] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [awaitingKey, setAwaitingKey] = useState(false);
  const {startOrcIdLogin} = useOrcIdLogin();

  const [createToken] = useMutation(CreateTokenDocument);
  const [createAssertionOptions] = useMutation(CreateAssertionOptionsDocument);

  const login = async () => {
    setError(null);
    let values;
    try {
      values = await form.validateFields();
    } catch { return }

    setLoading(true);
    try {
      let res = await createToken({ variables: { input: values } });
      let payload = res.data?.createToken;
      if (!payload) return;

      if (payload.mfaRequired) {
        // Second factor: get a challenge, have the authenticator sign it, then retry.
        const optionsRes = await createAssertionOptions({ variables: { input: values } });
        const assertionOptions = optionsRes.data?.createAssertionOptions;
        if (!assertionOptions?.success || !assertionOptions.options) {
          setError("Could not start security key verification");
          return;
        }

        let assertion;
        setAwaitingKey(true);
        try {
          assertion = await startAuthentication({ optionsJSON: JSON.parse(assertionOptions.options) });
        } catch {
          setError("Security key verification was cancelled or failed");
          return;
        } finally {
          setAwaitingKey(false);
        }

        res = await createToken({
          variables: { input: { ...values, assertionResponse: JSON.stringify(assertion) } }
        });
        payload = res.data?.createToken;
        if (!payload) return;

        if (!payload.token) {
          setError("Security key verification failed");
          return;
        }
      }

      if (!payload.token) {
        setError("Invalid username or password");
        return;
      }

      setToken(payload.token);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return <Modal open={open}
                footer={false}
                onCancel={() => onClose(true)}
                title="Login">
    { register === null && <>
        <Button type="link" style={{ paddingLeft: "0" }} onClick={startOrcIdLogin}>Login with ORCID</Button>
        <VerticalSpace />
        <Form layout="vertical" form={form}
              requiredMark={false}
              onFinish={login}>
            <Form.Item name="username" label="E-mail address"
                       rules={[{ required: true, message: 'Please enter your e-mail address' }]}>
                <Input type="email" disabled={loading} />
            </Form.Item>
            <Form.Item name="password" label="Password"
                       rules={[{ required: true, message: 'Please enter your password' }]}>
                <Input type="password" disabled={loading} />
            </Form.Item>
          { awaitingKey && <div style={{ marginBottom: 15 }}>Follow your browser's prompt to use your security key or passkey…</div> }
          { error && <div style={{ color: 'darkred', marginBottom: 15 }}>{error}</div> }
            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Login
                    </Button>
                    <Button onClick={() => onClose(true)}>
                        Cancel
                    </Button>
                    <Button type="link" onClick={() => setRegister(false)}>Create new account</Button>
                    <Button type="link" onClick={() => setRegister(true)}>Reset password</Button>
                </Space>
            </Form.Item>
        </Form>
    </> }
    { register !== null && <RegisterControl onCancel={() => setRegister(null)} isReset={register} /> }
  </Modal>
}